import * as React from "react";
import { forwardRef } from "react";
import { styled, SxProps, Theme } from "@mui/material/styles";
import MuiSwitch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { FONT_SIZES } from "../../constants/styles/typography";

/**
 * 토글 스위치 컴포넌트의 props — MUI `Switch` + `FormControlLabel` 래퍼.
 *
 * `labelPlacement="top"` 일 때 스위치를 270도 회전시켜 세로형 토글로 노출한다
 * (스위치 트랙이 수직 방향). 라벨/스위치 위치 관계가 시각적으로 직관적이지 않을 수 있으므로 주의.
 *
 * @example
 * ```tsx
 * <Switch label="활성화" checked={isActive} onChange={(e) => setActive(e.target.checked)} />
 * <Switch label="표시" labelPlacement="top" />
 * ```
 */
export interface SwitchProps {
  label?: string;
  /** 라벨 위치 — `"top"` 시 스위치가 270도 회전되어 세로형으로 표시 */
  labelPlacement?: "bottom" | "end" | "start" | "top";
  checked?: boolean;
  disabled?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  color?:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  size?: "small" | "medium";
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  inputRef?: React.Ref<HTMLInputElement>;
  sx?: SxProps<Theme>;
  /** 테스트 앵커. 루트로 전파된다(`...props`). 값 규약은 `src/test/testIds.ts` 참조. */
  "data-testid"?: string;
}

// labelPlacement에 따른 size 자동 결정 함수
const getSizeByLabelPlacement = (
  labelPlacement: "bottom" | "end" | "start" | "top"
): "small" | "medium" => {
  switch (labelPlacement) {
    case "bottom":
      return "medium";
    case "start":
    case "top":
      return "small";
    case "end":
    default:
      return "medium";
  }
};

// 회전을 통한 세로형 Switch를 위한 스타일드 컴포넌트
const RotatedSwitch = styled(MuiSwitch)(() => ({
  transform: "rotate(270deg) translateX(-7px)",
  transformOrigin: "center",
}));

// labelPlacement에 따른 폰트 사이즈를 적용하는 스타일드 FormControlLabel
const StyledFormControlLabel = styled(FormControlLabel)<{
  labelPlacement: "bottom" | "end" | "start" | "top";
}>(({ labelPlacement }) => {
  let fontSize: string;

  switch (labelPlacement) {
    case "bottom":
      fontSize = "15px";
      break;
    case "start":
      fontSize = FONT_SIZES.sm; // 14px
      break;
    case "top":
      fontSize = FONT_SIZES.xs; // 12px
      break;
    case "end":
    default:
      fontSize = "15px";
      break;
  }

  return {
    "& .MuiFormControlLabel-label": {
      fontSize,
      fontWeight: "SemiBold",
    },
  };
});

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      label,
      labelPlacement = "end",
      checked = false,
      disabled = false,
      onChange,
      color = "primary",
      size,
      inputProps,
      inputRef,
      sx,
      ...props
    },
    ref
  ) => {
    // labelPlacement에 따른 size 자동 결정 (size가 명시적으로 전달되지 않은 경우)
    const finalSize = size || getSizeByLabelPlacement(labelPlacement);

    // MUI Switch 는 언제나 `slotProps.input` 을 내려보내 `inputProps` 를 덮는다. `slotProps.input` 은
    // 그 기본값을 통째로 대체하므로 role 을 함께 넘겨야 이름과 switch 의미가 모두 남는다.
    const inputSlotProps = { input: { role: "switch", ...inputProps } };

    // top 위치일 때는 회전된 Switch 사용
    const SwitchComponent =
      labelPlacement === "top" ? RotatedSwitch : MuiSwitch;

    // 라벨이 있는 경우 StyledFormControlLabel으로 래핑
    if (label) {
      return (
        <StyledFormControlLabel
          control={
            <SwitchComponent
              ref={ref}
              checked={checked}
              disabled={disabled}
              onChange={onChange}
              color={color}
              size={finalSize}
              slotProps={inputSlotProps}
              inputRef={inputRef}
              {...props}
              sx={sx}
            />
          }
          label={label}
          labelPlacement={labelPlacement}
        />
      );
    }

    // 라벨이 없는 경우 Switch만 반환
    return (
      <SwitchComponent
        ref={ref}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        color={color}
        size={finalSize}
        slotProps={inputSlotProps}
        inputRef={inputRef}
        {...props}
        sx={sx}
      />
    );
  }
);

Switch.displayName = "Switch";
